import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";

import { isReservedUrlKey, warnIfReservedKey } from "./reservedKeys";
import { UniformFilterObject } from "./types";

export interface GalleryUrlState {
    filters: Record<string, string[]>;
    query: string;
    sortId: string | undefined;
}

const SEARCH_KEY = "q";
const SORT_KEY = "sort";

function readSearchParams(search: string): URLSearchParams {
    return new URLSearchParams(search);
}

function getMulti(params: URLSearchParams, key: string): string[] {
    const raw = params.get(key);
    return raw ? raw.split(";").filter(Boolean) : [];
}

/** Reads gallery state from a URL search string. Skips filters with reserved IDs. */
export function readStateFromUrl(
    search: string,
    filterDefs: FilterDefinition[]
): GalleryUrlState {
    const params = readSearchParams(search);
    const filters: Record<string, string[]> = {};

    for (const def of filterDefs) {
        if (warnIfReservedKey(def.id, "filter id")) continue;
        const values = getMulti(params, def.id);
        if (values.length > 0) filters[def.id] = values;
    }

    return {
        filters,
        query: params.get(SEARCH_KEY) ?? "",
        sortId: params.get(SORT_KEY) ?? undefined
    };
}

/**
 * Builds a query string from gallery state. Reserved keys (`q`, `sort`) are
 * always written first; filter keys follow.
 *
 * Returns the empty string when state is fully empty (no filters, no query, no sort).
 */
export function buildQueryString(state: GalleryUrlState): string {
    const params = new URLSearchParams();

    if (state.query) params.set(SEARCH_KEY, state.query);
    if (state.sortId) params.set(SORT_KEY, state.sortId);

    for (const [key, values] of Object.entries(state.filters)) {
        if (isReservedUrlKey(key)) continue;
        if (!values || values.length === 0) continue;
        params.set(key, values.join(";"));
    }

    return paramsToUnencodedString(params);
}

/**
 * Serialises params with semicolons left unencoded inside values
 * (mirrors roadmap-features/src/context/url.ts).
 */
function paramsToUnencodedString(params: URLSearchParams): string {
    return Array.from(params.entries())
        .map(([k, v]) => {
            const encodedKey = encodeURIComponent(k);
            const encodedValue = v.split(";").map(encodeURIComponent).join(";");
            return `${encodedKey}=${encodedValue}`;
        })
        .join("&");
}

/** Pushes the new state to the URL via history.pushState. No-op on the server. */
export function writeStateToUrl(state: GalleryUrlState): void {
    if (typeof window === "undefined") return;

    const queryString = buildQueryString(state);
    const newUrl = `${window.location.pathname}${
        queryString ? `?${queryString}` : ""
    }${window.location.hash}`;

    if (
        newUrl !==
        `${window.location.pathname}${window.location.search}${window.location.hash}`
    ) {
        window.history.pushState({}, "", newUrl);
    }
}

/** Converts a flat filters map (id → committed values) into UniformFilterObject shape. */
export function filtersToUniformFilterObject(
    flat: Record<string, string[]>,
    seedFilterObject: UniformFilterObject
): UniformFilterObject {
    const result: UniformFilterObject = {};

    for (const [filterId, subFilter] of Object.entries(seedFilterObject)) {
        const activeValues = new Set(flat[filterId] ?? []);
        const next: typeof subFilter = {};
        for (const optionKey of Object.keys(subFilter ?? {})) {
            const isActive = activeValues.has(optionKey);
            next[optionKey] = {
                committedActive: isActive,
                stagedActive: isActive
            };
        }
        result[filterId] = next;
    }

    return result;
}

/** Extracts the active committed values per filter as a flat map (for URL writing). */
export function uniformFilterObjectToFlat(
    state: UniformFilterObject
): Record<string, string[]> {
    const flat: Record<string, string[]> = {};
    for (const [filterId, subFilter] of Object.entries(state)) {
        const active = Object.entries(subFilter ?? {})
            .filter(([, opt]) => opt.committedActive)
            .map(([key]) => key);
        if (active.length > 0) flat[filterId] = active;
    }
    return flat;
}
